import React, { useEffect, useState } from 'react';
import { GovernanceBoard } from './governance-board';
import { DiscoveryBoard } from './discovery-board';
import PortfolioDashboard from './portfolio-dashboard';
import { useAuth } from '../hooks/useAuth';
import { getProjects } from '../api/client';
import NewIdea from './idea-new';
import IdeaDetail from './idea-detail';
import MonthlyReportForm from '../components/monthly-report-form';
import { ProjectRow } from '../../lib/types';

export default function Index() {
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showNewIdeaForm, setShowNewIdeaForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'discovery' | 'governance' | 'portfolio'>('discovery');
  const [showMonthlyReportForm, setShowMonthlyReportForm] = useState(false);
  const [monthlyReportProjectId, setMonthlyReportProjectId] = useState<string | null>(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getProjects();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (!user && !isDev) {
    return <div className="error">Not authenticated. Please log in via Cloudflare Access.</div>;
  }

  // Discovery: all work in discovery phase (both Product and NonProduct)
  const projectsInDiscovery = projects.filter(
    p => ['NewIdea', 'GatheringFeedback', 'ReadyForGovernance'].includes(p.Status)
  );
  // Governance: all work in assessment phase
  const projectsInGovernance = projects.filter(
    p => ['UnderGovernanceReview', 'BelowTheLine', 'AboveTheLine'].includes(p.Status)
  );
  // Portfolio: all approved/in-delivery work, plus below-the-line as secondary
  const projectsInPortfolio = projects.filter(
    p => ['InValidation', 'InPlanning', 'ReadyForDelivery', 'InDelivery', 'Complete', 'BelowTheLine'].includes(p.Status)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div className="container" style={{ flex: 1 }}>
        {loading ? (
          <div className="loading">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="content">
            <div className="empty-state">
              <p>No projects yet. Click "+ New Idea" to get started.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="tab-navigation">
              <button
                className={`tab-button ${activeTab === 'discovery' ? 'active' : ''}`}
                onClick={() => setActiveTab('discovery')}
              >
                Discovery
                {projectsInDiscovery.length > 0 && (
                  <span className="tab-badge">{projectsInDiscovery.length}</span>
                )}
              </button>
              <button
                className={`tab-button ${activeTab === 'governance' ? 'active' : ''}`}
                onClick={() => setActiveTab('governance')}
              >
                Governance
                {projectsInGovernance.length > 0 && (
                  <span className="tab-badge">{projectsInGovernance.length}</span>
                )}
              </button>
              <button
                className={`tab-button ${activeTab === 'portfolio' ? 'active' : ''}`}
                onClick={() => setActiveTab('portfolio')}
              >
                Portfolio
                {projectsInPortfolio.length > 0 && (
                  <span className="tab-badge">{projectsInPortfolio.length}</span>
                )}
              </button>
            </div>

            <div className="content">
              {activeTab === 'discovery' ? (
                <>
                  <DiscoveryBoard
                    projects={projectsInDiscovery}
                    onSelectProject={setSelectedProjectId}
                  />
                  {projectsInDiscovery.length === 0 && (
                    <div className="empty-state" style={{ marginTop: '2rem' }}>
                      <p>No work in discovery phase. Click "+ New Idea" to start.</p>
                    </div>
                  )}
                </>
              ) : activeTab === 'governance' ? (
                <>
                  <GovernanceBoard
                    projects={projectsInGovernance}
                    onSelectProject={setSelectedProjectId}
                  />
                  {projectsInGovernance.length === 0 && (
                    <div className="empty-state" style={{ marginTop: '2rem' }}>
                      <p>No projects in governance assessment. Move work from discovery when ready for review.</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <PortfolioDashboard />
                  {projectsInPortfolio.length > 0 && (
                    <>
                      <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Portfolio Status</h3>
                      <GovernanceBoard
                        projects={projectsInPortfolio}
                        onSelectProject={setSelectedProjectId}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {selectedProjectId && (
        <IdeaDetail
          projectId={selectedProjectId}
          onClose={() => setSelectedProjectId(null)}
          onUpdate={() => fetchProjects()}
        />
      )}

      {showNewIdeaForm && (
        <NewIdea
          onSubmitSuccess={() => {
            setShowNewIdeaForm(false);
            fetchProjects();
          }}
          onCancel={() => setShowNewIdeaForm(false)}
        />
      )}

      {showMonthlyReportForm && monthlyReportProjectId && (
        <MonthlyReportForm
          projectId={monthlyReportProjectId}
          projectTitle={projects.find(p => p.ProjectId === monthlyReportProjectId)?.Title || ''}
          onSuccess={() => {
            setShowMonthlyReportForm(false);
            setMonthlyReportProjectId(null);
            fetchProjects();
          }}
          onCancel={() => {
            setShowMonthlyReportForm(false);
            setMonthlyReportProjectId(null);
          }}
        />
      )}
    </div>
  );
}
